import type { WebContainer, WebContainerProcess } from '@webcontainer/api';
import { appendTerminalLine, appendTerminalProcessOutput } from '../../stores/terminal';
import { workbenchStore, type WorkbenchBuildFailure, type WorkbenchBuildFailurePhase } from '../../stores/workbench';
import {
  waitForTemplateReady,
  recordProductionBuildOutcome,
  markTemplateDependenciesInstalled,
  ensureDevServerRunning,
  getMountedDepsLockHash,
  getPreviewPort,
  restartDevServerAfterSnapshot,
  stopDevServerForRestore,
} from '../../webcontainer/index';
import { detectWorkbenchCapability } from '../../webcontainer/capability';
import { getTemplateLockHash } from '../../webcontainer/depsCache';
import { npmFrozenInstallCliArgs, webcontainerFileExistsUtf8 } from '../../webcontainer/npmFrozenInstall';
import { wcPerfMark, wcPerfMeasure } from '../../utils/workbenchPerf';
import { recordWorkbenchMemory } from '../../utils/workbenchMemoryTelemetry';
import { flushDirtyEditorFilesBeforeStream } from './writeFile';
import { formatViteDevCheckErrors, runViteDevCheck } from './viteDevCheck';
import {
  WORKBENCH_FS_DEADLINE_MS,
  WORKBENCH_SPAWN_DEADLINE_MS,
  WorkbenchUnresponsiveError,
  isWebContainerResponsive,
  withDeadline,
} from '../../webcontainer/deadline';

/** Strip ANSI escape sequences (colors, cursor, line-clear) so the LLM receives clean error text. */
export function stripAnsi(s: string): string {
  // CSI sequences: ESC [ ... (letter)  — covers colors, cursor, erase-line, etc.
  // OSC sequences: ESC ] ... (BEL or ST)
  // Simple two-char escapes: ESC (char)
  return s
    .replace(/\u001b\[[0-9;]*[A-Za-z]/g, '')
    .replace(/\u001b\][^\u0007\u001b]*(?:\u0007|\u001b\\)/g, '')
    .replace(/\u001b[^[\\]]/g, '');
}

/**
 * Extract a stable signature from build errors for repeat-detection.
 * Matches the first few ERROR/error lines so minor whitespace changes don't
 * fool the deduplication.
 */
export function extractErrorSignature(errors: string): string {
  const lines = errors.split('\n');
  const meaningful = lines
    .filter((l) => l.includes('ERROR:') || l.includes('error'))
    .map((l) => l.trim())
    .slice(0, 3);
  return meaningful.join('|') || errors.slice(0, 200);
}

export interface BuildResult {
  ok: boolean;
  /** Raw terminal output (may contain ANSI codes). Used for the UI/terminal. */
  errors: string;
  output: string;
  phase?: WorkbenchBuildFailurePhase;
  /** LLM-ready formatted string — ANSI stripped, human-readable. Passed to addToolOutput. */
  formattedOutput: string;
  /** Stable error signature for repeat-error detection in the facade. */
  errorSig: string;
  /**
   * The WebContainer stopped responding, so the build could not run. Not a code failure:
   * callers must not ask the model to fix it or count it as a failed attempt.
   */
  environment?: boolean;
}

function emitBuildFailure(phase: WorkbenchBuildFailurePhase, errors: string, output: string): void {
  const detail: WorkbenchBuildFailure = { phase, errors, output, at: Date.now() };
  workbenchStore.lastBuildFailure.set(detail);
  if (typeof globalThis.window !== 'undefined') {
    globalThis.window.dispatchEvent(new CustomEvent('workbench:build-failed', { detail }));
    if (phase === 'tsc') {
      globalThis.window.dispatchEvent(new CustomEvent('workbench:tsc-failed', { detail }));
    }
  }
}

/** No `workbench:build-failed` event: that event offers a code fix, and the code is not at fault. */
function environmentBuildResult(message: string): BuildResult {
  appendTerminalLine(message, 'stderr');
  return finalizeBuildResult({
    ok: false,
    environment: true,
    errors: message,
    output: message,
    formattedOutput: message,
    errorSig: '',
  });
}

function finalizeBuildResult(result: BuildResult): BuildResult {
  recordProductionBuildOutcome(result.ok);
  recordWorkbenchMemory('build', { extra: { ok: result.ok, phase: result.phase ?? null } });
  return result;
}

function failedBuild(
  phase: WorkbenchBuildFailurePhase,
  label: string,
  rawOutput: string,
  llmPrefix: string,
): BuildResult {
  const errors = `${label}:\n${rawOutput}`;
  emitBuildFailure(phase, errors, rawOutput);
  const clean = stripAnsi(rawOutput);
  return finalizeBuildResult({
    ok: false,
    errors,
    output: rawOutput,
    phase,
    formattedOutput: `${llmPrefix}:\n${clean}`,
    errorSig: extractErrorSignature(clean),
  });
}

/** A stale/cancelled build never reaches this — callers should drop it entirely. */
export class BuildAbortedError extends Error {
  constructor() {
    super('Build cancelled: the turn that requested it was superseded.');
    this.name = 'BuildAbortedError';
  }
}

/**
 * Waits for a spawned process to exit, but gives up (and kills the process) after
 * `timeoutMs` instead of hanging the whole edit turn forever if `npm install`/`vite
 * build`/`tsc` gets stuck inside the WebContainer.
 */
async function waitForExitWithTimeout(
  proc: WebContainerProcess,
  timeoutMs: number,
  label: string,
): Promise<number> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      void proc.kill();
      reject(new Error(`${label} did not finish within ${Math.round(timeoutMs / 1000)}s and was killed.`));
    }, timeoutMs);
  });
  try {
    return await Promise.race([proc.exit, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Spawns a process, collects its output, and kills it on `signal` or timeout. */
async function runProcess(
  wc: WebContainer,
  command: string,
  args: string[],
  opts: {
    timeoutMs: number;
    label: string;
    onOutput?: (chunk: string) => void;
    signal?: AbortSignal;
  },
): Promise<{ exitCode: number; output: string }> {
  const proc = await withDeadline(wc.spawn(command, args), WORKBENCH_SPAWN_DEADLINE_MS, opts.label);
  const onAbort = () => void proc.kill();
  opts.signal?.addEventListener('abort', onAbort);
  let output = '';
  const piping = proc.output.pipeTo(
    new WritableStream<string>({
      write(chunk) {
        output += chunk;
        opts.onOutput?.(chunk);
      },
    }),
  );
  try {
    const exitCode = await waitForExitWithTimeout(proc, opts.timeoutMs, opts.label);
    await piping;
    return { exitCode, output };
  } finally {
    opts.signal?.removeEventListener('abort', onAbort);
  }
}

const INSTALL_TIMEOUT_MS = 180_000;
const VITE_BUILD_TIMEOUT_MS = 120_000;
const TSC_TIMEOUT_MS = 90_000;

/** Kept in the WebContainer's `node_modules`, so it lasts the session and never enters a snapshot. */
const BUILD_CACHE_DIR = 'node_modules/.cache/ideoz';
const TSC_BUILD_INFO_PATH = `${BUILD_CACHE_DIR}/tsc.tsbuildinfo`;
/** Fallback `vite build` output; kept out of `dist/` so the dev server's watcher ignores it. */
const VITE_BUILD_CHECK_OUT_DIR = `${BUILD_CACHE_DIR}/vite-build-check`;
/** Lets Vite's watcher see the files the turn just wrote before the check requests them. */
const WATCHER_SETTLE_MS = 400;
/** Writing the founder's unsaved editor files into the container before the build. */
const EDITOR_FLUSH_DEADLINE_MS = 30_000;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** `node_modules` in this WebContainer came from the archive or install for the current lockfile. */
async function mountedDepsMatchLockfile(wc: WebContainer): Promise<boolean> {
  const mounted = getMountedDepsLockHash();
  if (!mounted) return false;
  try {
    const lock = await wc.fs.readFile('package-lock.json', 'utf-8');
    return getTemplateLockHash(lock) === mounted;
  } catch {
    return false;
  }
}

/**
 * Bundler-level check for the turn. Normally asks the running dev server to transform every
 * module reachable from `src/main.tsx` (`./viteDevCheck.ts`), which costs almost no extra
 * memory. Falls back to `vite build` only when the dev server cannot be reached.
 */
async function checkViteCompile(
  wc: WebContainer,
  onOutput: ((chunk: string) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<{ ok: true } | { ok: false; output: string }> {
  wcPerfMark('wb:build:vite:start');
  try {
    appendTerminalLine('$ vite compile check (running dev server)', 'info');
    if (await ensureDevServerRunning(wc)) {
      await sleep(WATCHER_SETTLE_MS);
      if (signal?.aborted) throw new BuildAbortedError();
      const result = await runViteDevCheck(wc, { port: getPreviewPort(), signal });
      if (signal?.aborted) throw new BuildAbortedError();
      if (result.status === 'passed') {
        appendTerminalLine(
          `Vite compiled ${result.checked} module${result.checked === 1 ? '' : 's'}.`,
          'info',
        );
        return { ok: true };
      }
      if (result.status === 'failed') {
        const output = formatViteDevCheckErrors(result.errors);
        appendTerminalProcessOutput(output, 'stderr');
        return { ok: false, output };
      }
      appendTerminalLine(
        `Dev server check unavailable (${result.reason}); running vite build instead.`,
        'info',
      );
    }

    if (signal?.aborted) throw new BuildAbortedError();
    appendTerminalLine('$ npx vite build', 'info');
    const { exitCode, output } = await runProcess(
      wc,
      'npx',
      ['vite', 'build', '--outDir', VITE_BUILD_CHECK_OUT_DIR, '--emptyOutDir'],
      { timeoutMs: VITE_BUILD_TIMEOUT_MS, label: 'vite build', onOutput, signal },
    );
    appendTerminalProcessOutput(output, exitCode !== 0 ? 'stderr' : 'stdout');
    return exitCode === 0 ? { ok: true } : { ok: false, output };
  } finally {
    wcPerfMark('wb:build:vite:end');
    wcPerfMeasure('wb:build:vite:start', 'wb:build:vite:end', 'wc:executeBuild:vite');
  }
}

/**
 * Per-turn build check: dependencies, a Vite compile check, then an incremental `tsc`. The two
 * checks never run at the same time. The full production `vite build` happens at Publish, on
 * the server (Chef's `deploy.ts`); nothing in the browser uses a local `dist/`.
 */
export async function executeBuild(
  webcontainer: WebContainer,
  onOutput?: (chunk: string) => void,
  signal?: AbortSignal,
): Promise<BuildResult> {
  if (signal?.aborted) throw new BuildAbortedError();
  // A frozen WebContainer would hang the first call below with no error.
  if (!(await isWebContainerResponsive(webcontainer))) {
    return environmentBuildResult(
      'The in-browser workspace is not responding, so the build could not run.',
    );
  }
  try {
    return await executeBuildSteps(webcontainer, onOutput, signal);
  } catch (err) {
    if (err instanceof WorkbenchUnresponsiveError) return environmentBuildResult(err.message);
    throw err;
  }
}

async function executeBuildSteps(
  webcontainer: WebContainer,
  onOutput: ((chunk: string) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<BuildResult> {
  if (signal?.aborted) throw new BuildAbortedError();

  appendTerminalLine(`── Build ${new Date().toLocaleTimeString()} ──`, 'info');
  workbenchStore.lastBuildFailure.set(null);

  wcPerfMark('wb:build:waitTemplate:start');
  await waitForTemplateReady();
  wcPerfMark('wb:build:waitTemplate:end');
  wcPerfMeasure('wb:build:waitTemplate:start', 'wb:build:waitTemplate:end', 'wc:executeBuild:waitTemplate');

  wcPerfMark('wb:build:flushEditor:start');
  const flushed = await withDeadline(
    flushDirtyEditorFilesBeforeStream(webcontainer),
    EDITOR_FLUSH_DEADLINE_MS,
    'Saving editor changes into the workspace',
  );
  wcPerfMark('wb:build:flushEditor:end');
  wcPerfMeasure('wb:build:flushEditor:start', 'wb:build:flushEditor:end', 'wc:executeBuild:flushEditor');
  if (flushed > 0) {
    appendTerminalLine(
      `Synced ${flushed} edited file${flushed === 1 ? '' : 's'} from the editor into the workspace.`,
      'info',
    );
  }

  if (signal?.aborted) throw new BuildAbortedError();

  if (workbenchStore.depsInstalled.get()) {
    appendTerminalLine('Dependencies already installed, skipping npm install.', 'info');
  } else if (await mountedDepsMatchLockfile(webcontainer)) {
    // The store flag is reset on remount, but the mounted `node_modules` still matches.
    appendTerminalLine('node_modules already matches package-lock.json, skipping npm install.', 'info');
    workbenchStore.depsInstalled.set(true);
    markTemplateDependenciesInstalled();
  } else {
    const hasLock = await webcontainerFileExistsUtf8(webcontainer, 'package-lock.json');
    const npmArgs = npmFrozenInstallCliArgs(hasLock);
    appendTerminalLine(`$ npm ${npmArgs.join(' ')}`, 'info');
    const npmStart = hasLock ? 'wb:build:npm:ci:start' : 'wb:build:npm:install:start';
    const npmEnd = 'wb:build:npm:end';
    wcPerfMark(npmStart);
    const install = await runProcess(webcontainer, 'npm', npmArgs, {
      timeoutMs: INSTALL_TIMEOUT_MS,
      label: 'npm install',
      onOutput,
      signal,
    });
    appendTerminalProcessOutput(install.output, install.exitCode !== 0 ? 'stderr' : 'stdout');
    wcPerfMark(npmEnd);
    wcPerfMeasure(npmStart, npmEnd, hasLock ? 'wc:executeBuild:npmCi' : 'wc:executeBuild:npmInstall');

    if (install.exitCode !== 0) {
      return failedBuild(
        'npm-install',
        `npm ${npmArgs[0]} failed`,
        install.output,
        `Build failed (npm ${npmArgs[0]})`,
      );
    }
    workbenchStore.depsInstalled.set(true);
    markTemplateDependenciesInstalled();
  }

  if (signal?.aborted) throw new BuildAbortedError();

  const compile = await checkViteCompile(webcontainer, onOutput, signal);
  if (!compile.ok) {
    return failedBuild('vite', 'Vite build failed', compile.output, 'Build failed');
  }

  if (signal?.aborted) throw new BuildAbortedError();

  // Best-effort devices: the dev server plus tsc is the largest memory peak of a build step.
  const stopDevServerForTsc = detectWorkbenchCapability().tier === 'best-effort';
  let tsc: { exitCode: number; output: string };
  try {
    if (stopDevServerForTsc) await stopDevServerForRestore();
    appendTerminalLine('$ npx tsc --noEmit --incremental', 'info');
    wcPerfMark('wb:build:tsc:start');
    await withDeadline(
      webcontainer.fs.mkdir(BUILD_CACHE_DIR, { recursive: true }).catch(() => undefined),
      WORKBENCH_FS_DEADLINE_MS,
      'Preparing the type check',
    );
    tsc = await runProcess(
      webcontainer,
      'npx',
      ['tsc', '--noEmit', '--incremental', '--tsBuildInfoFile', TSC_BUILD_INFO_PATH],
      { timeoutMs: TSC_TIMEOUT_MS, label: 'tsc --noEmit', onOutput, signal },
    );
    appendTerminalProcessOutput(tsc.output, tsc.exitCode !== 0 ? 'stderr' : 'stdout');
    wcPerfMark('wb:build:tsc:end');
    wcPerfMeasure('wb:build:tsc:start', 'wb:build:tsc:end', 'wc:executeBuild:tsc');
  } finally {
    if (stopDevServerForTsc) void restartDevServerAfterSnapshot(webcontainer);
  }

  if (tsc.exitCode !== 0) {
    return failedBuild('tsc', 'TypeScript type-check failed', tsc.output, 'TypeScript check failed');
  }

  appendTerminalLine('Build succeeded.', 'info');
  workbenchStore.lastBuildFailure.set(null);

  if (signal?.aborted) throw new BuildAbortedError();

  if (!stopDevServerForTsc) {
    try {
      await ensureDevServerRunning(webcontainer);
    } catch (err: unknown) {
      appendTerminalLine(
        `Failed to start preview server: ${err instanceof Error ? err.message : String(err)}`,
        'stderr',
      );
    }
  }

  return finalizeBuildResult({
    ok: true,
    errors: '',
    output: 'Build succeeded.',
    formattedOutput: 'Build succeeded.',
    errorSig: '',
  });
}
